import * as yup from 'yup';
import { z } from 'zod';

import { PIN_LENGTH, SSN_LENGTH } from '@clearverify/constants';

import type { AttestationsListQuery } from 'src/api';
import { AddressFieldsFormFields } from 'src/constants/address';
import { EmployeeBasicInfoFormField, PinFormField, SsnFormField } from 'src/constants/signup';
import {
  passwordValidationZodSchema,
  phoneNumberZodValidationSchema,
  zipCodeZodValidationSchema,
} from 'src/constants/validation';
import { EmployeeBasicInfoFormValues, PinFormValues, SsnFormValues } from 'src/types/signup';

import { ConsumerVerificationPreferenceFormFields } from '../types';

export const ssnFormValidationSchema = z.object({
  [SsnFormField.ssn]: z.string().length(SSN_LENGTH, 'Invalid SSN'),
}) satisfies z.ZodType<SsnFormValues>;

export const pinFormValidationSchema = z.object({
  [PinFormField.pin]: z.string().length(PIN_LENGTH, 'Invalid PIN'),
}) satisfies z.ZodType<PinFormValues>;

export const employeeBasicInfoFormValidationSchema = z
  .object({
    [EmployeeBasicInfoFormField.ssn]: z.string().length(SSN_LENGTH, 'Invalid SSN'),
    [EmployeeBasicInfoFormField.firstName]: z.string().nonempty('Required'),
    [EmployeeBasicInfoFormField.lastName]: z.string().nonempty('Required'),
    [EmployeeBasicInfoFormField.email]: z.email().nonempty('Required'),
    [EmployeeBasicInfoFormField.phoneNumber]: phoneNumberZodValidationSchema,
    [EmployeeBasicInfoFormField.password]: passwordValidationZodSchema,
    [EmployeeBasicInfoFormField.confirmPassword]: passwordValidationZodSchema.nonempty('Required'),
    [EmployeeBasicInfoFormField.homeAddress]: z.object({
      [AddressFieldsFormFields.street]: z.string('Required street'),
      [AddressFieldsFormFields.city]: z.string('Required city'),
      [AddressFieldsFormFields.county]: z.string().nullable(),
      [AddressFieldsFormFields.state]: z.string('Required state'),
      [AddressFieldsFormFields.zipCode]: zipCodeZodValidationSchema,
    }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: [EmployeeBasicInfoFormField.confirmPassword],
  }) satisfies z.ZodType<EmployeeBasicInfoFormValues>;

export const consumerAttestationsFormValidationSchema = (
  items?: AttestationsListQuery['attestationList'],
) => {
  const validationStep = items?.reduce((acc, item) => {
    return { ...acc, [item.name as string]: yup.boolean().required().isTrue('Required') };
  }, {} as yup.ObjectShape);
  return yup.object(validationStep);
};

export const consumerVerificationPreferenceFormValidationSchema = yup.object({
  [ConsumerVerificationPreferenceFormFields.isAutoVerificationRequestEnabled]: yup
    .string()
    .nullable(),
});
